<template>
    <v-container class="pa-5" fluid>
        <ConfirmDialog
            :is-open="showConfirmDialog"
            :data="confirmDialogData"
            @on-confirm="confirmDialogConfirm"
            @on-abort="confirmDialogClose"
        >
            <template #title>
                {{ confirmDialogData.title }}
            </template>
            <template #content>
                {{ confirmDialogData.content }}
            </template>
        </ConfirmDialog>
        <NotificationDialog
            :is-open="saveNotificationDialog"
            :action-name="actionName"
            :dock-reservation-title="dockReservationTitle"
            :current-dock-reservation="currentDockReservation"
            @close-notification-dialog="closeNotificationDialog"
        />
        <DeleteNotificationDialog
            :is-open="deleteNotificationDialog"
            @close-delete-notification-dialog="closeDeleteNotificationDialog"
        />

        <page-title>Awizacje dostaw</page-title>
        <v-row>
            <v-col cols="12" class="text-right">
                <v-btn
                    v-if="canCreateNotification"
                    color="secondary"
                    fab
                    small
                    class="elevation-1"
                    data-sentry-label="Dodaj"
                    @click="showCreateNotificationDialog()"
                >
                    <v-icon>
                        mdi-plus
                    </v-icon>
                </v-btn>
                <v-btn
                    v-if="canUpdateNotification"
                    color="teal"
                    fab
                    small
                    class="elevation-1 ml-1"
                    data-sentry-label="Edytuj"
                    @click="showEditNotificationDialog()"
                >
                    <v-icon color="white">
                        mdi-pencil
                    </v-icon>
                </v-btn>
                <v-btn
                    v-if="canDeleteNotification"
                    color="pink"
                    fab
                    small
                    class="elevation-1 ml-1"
                    data-sentry-label="Usuń"
                    @click="showDeleteNotificationDialog()"
                >
                    <v-icon color="white">
                        mdi-delete
                    </v-icon>
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="2">
                <v-card
                    v-if="!dockReservationsLoading"
                >
                    <v-card-title>Schowek</v-card-title>
                    <v-divider />

                    <v-data-table
                        id="dragContainer"
                        ref="container"
                        :headers="clipboardHeaders"
                        :items="dockClipboardReservations"
                        hide-default-header
                        hide-default-footer
                    >
                        <template #item="{item}">
                            <tr
                                class="clipboardEvent"
                                :data-id="item.id"
                                :data-duration="getDuration(item.duration)"
                            >
                                <td>
                                    <v-icon color="grey">
                                        mdi-arrow-all
                                    </v-icon>
                                    {{ item.title }}
                                </td>
                                <td>
                                    <v-btn
                                        v-if="canUpdateNotification"
                                        icon
                                        data-sentry-label="Edytuj"
                                        @click="showEditClipboardReservationDialog(item)"
                                    >
                                        <v-icon color="green">
                                            mdi-pencil
                                        </v-icon>
                                    </v-btn>
                                    <v-btn
                                        v-if="canUpdateNotification"
                                        icon
                                        data-sentry-label="Edytuj"
                                        @click="tryDeleteReservation(item, $event)"
                                    >
                                        <v-icon color="pink">
                                            mdi-delete
                                        </v-icon>
                                    </v-btn>
                                </td>
                            </tr>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
            <v-col cols="10">
                <v-card>
                    <FullCalendar
                        v-if="!dockReservationsLoading"
                        ref="fullCalendar"
                        class="fc fc-ltr fc-unthemed fc-colours"
                        :options="{
                            ...calendarOptions,
                            resources: docks,
                            events: dockReservations,
                            slotMinTime: minTime,
                            slotMaxTime: maxTime,
                            slotDuration: slotDurationNotification
                        }"
                    />
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import PageTitle from '@/components/common/PageTitle'
import FullCalendar from '@fullcalendar/vue'
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'
import { Draggable } from '@fullcalendar/interaction'
import interaction from '@fullcalendar/interaction'
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid'
// import scrollgrid from '@fullcalendar/scrollgrid'
import { errorHandler } from '@/utils/errorHandler'
import store from '@/store'
import NotificationDialog from './NotificationDialog'
import DeleteNotificationDialog from '@/views/Shipping/common/DeleteNotificationDialog'
import { hasUserPermission } from '@/utils/permissionsHandler'
import calendar from '@/mixins/calendar'
import { appConfig } from '@/config'
import { resolve } from '@sentry/utils'
import { format } from 'date-fns'

export default {
    name: 'NotificationList',
    components: {
        PageTitle,
        FullCalendar,
        ConfirmDialog,
        NotificationDialog,
        DeleteNotificationDialog
    },
    mixins: [ calendar ],
    beforeRouteEnter(to, from, next) {
        store.dispatch('shipping/notification/clearDocks')
        next()
    },
    data() {
        return {
            dockReservationsLoading: false,
            calendarOptions: {
                plugins: [interaction, resourceTimeGridPlugin],
                //plugins: [interaction, scrollgrid, resourceTimeGridPlugin],
                schedulerLicenseKey: appConfig.common.schedulerLicenseKey,
                customButtons: {
                    prev: {
                        text: 'prev',
                        click: () => this.getDataOnWeekChangeButton('prev')
                    },
                    next: {
                        text: 'next',
                        click: () => this.getDataOnWeekChangeButton('next')
                    }
                },
                headerToolbar: {
                    left: 'prev',
                    center: 'title',
                    right: 'prev, next'
                },
                initialView: 'resourceTimeGridWeek',
                allDaySlot: false,
                firstDay: 1,
                slotLabelFormat: {hour: 'numeric', minute: '2-digit', hour12: false},
                locale: 'pl',
                displayEventTime: false,
                height: 'auto',
                loading: this.dockReservationsLoading,
                selectable: true,
                eventClick: this.showNotificationDialog,
                editable: false,
                droppable: true,
                eventReceive: (info) => this.addFromClipboard(info),
                eventOverlap: () => false,
                dayHeaderFormat: this.dayHeaderFormat,
                // dayMinWidth: 50 scrollgrid
            },
            saveNotificationDialog: false,
            deleteNotificationDialog: false,
            dockReservationTitle: null,
            actionName: null,
            user: null,
            currentDockReservation: {
                info: null,
                dockReservationData: null
            },
            confirmDialogData: {
                confirm: () => {},
            },
            showConfirmDialog: false,
            clipboardController: null,
            clipboardHeaders: [
                {
                    text: 'Reservation',
                    value: 'title',
                },
                {
                    text: 'Actions',
                    value: 'action',
                }
            ]
        }
    },
    computed: {
        dockReservations() {
            return this.$store.state.shipping.notification.notification.dockReservations
        },
        dockClipboardReservations() {
            return this.$store.state.shipping.notification.notification.dockClipboardReservations
        },
        docks() {
            return this.$store.state.shipping.notification.notification.docks
        },
        userId() {
            return this.$store.state.user.userId
        },
        canCreateNotification() {
            return hasUserPermission('notification_all_create') || hasUserPermission('notification_create')
        },
        canReadNotification() {
            return hasUserPermission('notification_all_read') || hasUserPermission('notification_read')
        },
        canDeleteNotification() {
            return hasUserPermission('notification_all_delete') || hasUserPermission('notification_delete')
        },
        canUpdateNotification() {
            return hasUserPermission('notification_all_update') || hasUserPermission('notification_update')
        },
        isWarehouseUser() {
            return hasUserPermission('notification_all_update') || hasUserPermission('notification_all_create')
        },
        minTime() {
            return appConfig.shipping.notification.minTime
        },
        maxTime() {
            return appConfig.shipping.notification.maxTime
        },
        slotDurationNotification() {
            return appConfig.shipping.notification.slotDurationNotification
        }
    },
    mounted() {
        store.dispatch('toggleMainLoader', true)
        this.dockReservationsLoading = true

        const queryDockType = {query: '?dockType=10', dockType: 10}

        Promise.all([
            this.$store.dispatch('shipping/notification/getDocksDicts', queryDockType),
            this.$store.dispatch('shipping/notification/getDockClipboardReservations', queryDockType),
            this.$store.dispatch('administration/getUser', this.userId )
        ])
            .then(response => {
                this.getDockReservations(this.currentWeekDateRange())
                this.$store.dispatch('shipping/notification/setUser', response[2].data)
                this.user = response[2].data
                if (this.user.profile.customerId) {
                    response[0].results.forEach(dock => {
                        this.$store.dispatch('shipping/notification/getDockOccupation', {
                            customerId: this.user.profile.customerId,
                            date: `?start=${this.currentWeekDateRange()[0]}&end=${this.currentWeekDateRange()[this.currentWeekDateRange().length-1]}`,
                            dockId: dock.id
                        }).then(response => {
                            this.$store.dispatch('shipping/notification/setBusinessHours', {data: response.data, weekRange: this.currentWeekDateRange(), dockId: dock.id})
                        })
                            .catch(error => {
                                errorHandler(error)
                            })
                    })
                }
            })
            .catch(error => {
                errorHandler(error)
            })
            .finally(() => {
                this.dockReservationsLoading = false
                store.dispatch('toggleMainLoader', false)
            })
    },
    updated() {
        let container

        if (this.$refs.container?.$el && !this.clipboardController) {
            container = this.$refs.container.$el

            this.clipboardController = new Draggable(container, {
                itemSelector: '.clipboardEvent',
                eventData: function(el) {
                    const id = el.getAttribute('data-id')
                    const title = el.textContent
                    const duration = el.getAttribute('data-duration')

                    return {...el, title, duration, id}
                }
            })
        }

    },
    methods: {
        dayHeaderFormat(date) {
            return `${this.generate2CharWeekday(date.start.marker)}. ${format(new Date(date.start.marker), 'DD.MM')}`
        },
        getDuration(duration) {
            return duration === 60 ? '01:00' : '00:30'
        },
        getDataOnWeekChangeButton(direction) {
            this.$store.dispatch('shipping/notification/clearBusinessHours')
            const fullCalendarApi = this.$refs.fullCalendar.getApi()

            direction === 'next' ? fullCalendarApi.next() : fullCalendarApi.prev()
            const start = fullCalendarApi.view.activeStart
            const weekRange = this.generateWeekRange(start)

            this.getDockOccupationForSelectedWeekRange(weekRange)
            this.getDockReservations(weekRange)
        },
        getDockOccupationForSelectedWeekRange(weekRange) {
            if (this.user.profile.customerId) {
                this.docks.forEach(dock => {this.$store.dispatch('shipping/notification/getDockOccupation', {
                    customerId: this.user.profile.customerId,
                    date: `?start=${weekRange[0]}&end=${weekRange[weekRange.length-1]}`,
                    dockId: dock.id
                }).then(response => {
                    this.$store.dispatch('shipping/notification/setBusinessHours', {data: response.data, weekRange: weekRange, dockId: dock.id})
                })
                    .catch(error => {
                        errorHandler(error)
                    })
                })
            }

        },
        getCustomers() {
            this.$store.dispatch('customers/getCustomers', {
                paginate: '',
                query: '?isHidden=false'
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        getDockReservations(weekRange, callback = () => {}) {
            this.$store.dispatch('shipping/notification/getDockReservations', {
                query: `?start=${weekRange[0]}&end=${weekRange[weekRange.length-1]}&dockType=10`,
                dockType: 10
            })
                .then(callback)
                .catch(error => {
                    errorHandler(error)
                })
        },
        showCreateNotificationDialog(info) {
            if (this.isWarehouseUser) {
                this.getCustomers()
            }
            this.currentDockReservation.info = info
            this.saveNotificationDialog = true
            this.actionName = 'createDockReservation'
            this.dockReservationTitle = 'Tworzenie rezerwacji'
        },
        async getDockReservation(dockReservationId) {
            return await this.$store.dispatch('shipping/notification/getDockReservation', dockReservationId)
                .then(response => {
                    this.currentDockReservation.dockReservationData = response.data
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        async getClipboardReservation(id) {
            if (id === this.currentDockReservation?.dosckReservationData?.id) {
                return new Promise(() => {
                    resolve(true)
                })
            }

            return await this.$store.dispatch('shipping/notification/getClipboardReservation', id)
                .then(response => {
                    this.currentDockReservation.dockReservationData = response.data
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        showNotificationDialog(info) {
            this.getDockReservation(parseInt(info.event.id)).then(() => {
                this.currentDockReservation.info = info
                if (this.currentDockReservation.dockReservationData) {
                    this.saveNotificationDialog = true
                    this.actionName = 'openDockReservation'
                    this.dockReservationTitle = 'Szczegóły rezerwacji'
                }
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        updateCalendarView(callback = () => {}) {
            this.getDockReservations(this.generateWeekRange(this.$refs.fullCalendar.getApi().view.activeStart), callback)
            if (this.user.profile.customerId) {
                this.getDockOccupationForSelectedWeekRange(this.generateWeekRange(this.$refs.fullCalendar.getApi().view.activeStart))
            }
        },
        updateClipboard() {
            this.$store.dispatch('shipping/notification/getDockClipboardReservations', {query: '?dockType=10', dockType: 10})
        },
        closeNotificationDialog() {
            this.saveNotificationDialog = false
            this.currentDockReservation = {
                info: null,
                dockReservationData: null
            },
            this.updateCalendarView()
            this.updateClipboard()
        },
        showDeleteNotificationDialog() {
            this.deleteNotificationDialog = true
        },
        tryDeleteReservation(info, $event) {
            $event.stopPropagation()
            this.confirmDialogData = {
                title: 'Usuwanie rezerwacji',
                content: 'Czy na pewno chcesz usunąć rezerwację?',
                confirm: this.deleteReservation,
                data: info,
            }
            this.showConfirmDialog = true
        },
        confirmDialogConfirm(payload) {
            this.confirmDialogData.confirm(payload)
            this.confirmDialogClose()
        },
        confirmDialogClose() {
            this.showConfirmDialog = false
            setTimeout(() => {
                this.confirmDialogData = {
                    title: null,
                    content: null,
                    confirm: () => {},
                    data: null,
                }
            }, 500)
        },
        deleteReservation({ data }) {
            const action = data.inClipboard ? 'deleteClipboardNotification' : 'deleteNotification'

            this.$store.dispatch(`shipping/notification/${action}`, data.id )
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.updateCalendarView()
                    this.updateClipboard()
                })
        },
        closeDeleteNotificationDialog() {
            this.deleteNotificationDialog = false
            this.updateCalendarView()
        },
        showEditNotificationDialog() {
            if (this.isWarehouseUser) {
                this.getCustomers()
            }
            this.saveNotificationDialog = true
            this.actionName = 'updateDockReservation'
            this.dockReservationTitle = 'Edycja rezerwacji'
        },
        showEditClipboardReservationDialog(info) {
            this.currentDockReservation.info = info
            this.getClipboardReservation(info.id).then(() => {
                this.saveNotificationDialog = true
                this.actionName = 'updateClipboardReservation'
                this.dockReservationTitle = 'Edycja rezerwacji w schowku'
            })
        },
        addFromClipboard(el) {
            this.getClipboardReservation(el.event.id)
                .then(() => {
                    const payload = this.currentDockReservation.dockReservationData

                    payload.dockId = el.event.getResources().at(0).id
                    payload.inClipboard = false
                    payload.dockingStartDateTime = el.event.startStr
                    this.$store.dispatch('shipping/notification/updateClipboardNotification', payload)
                        .catch(error => {
                            errorHandler(error)
                        })
                        .finally(() => {
                            this.updateCalendarView(el.revert)
                        })
                })
                .finally(() => {
                    this.updateClipboard()
                })
        },
        toClipboard(el) {
            this.getDockReservation(el.event.id)
                .then(() => {
                    const payload = this.currentDockReservation.dockReservationData

                    payload.inClipboard = true
                    this.$store.dispatch('shipping/notification/updateNotification', payload)
                        .catch(error => {
                            errorHandler(error)
                        })
                        .finally(() => {
                            this.updateCalendarView(el.revert)
                        })
                })
                .finally(() => {
                    this.updateClipboard()
                })
        },
        deleteEditNotificationDialog() {
            this.saveNotificationDialog = false
        },
    }
}

</script>

